import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding } from "./onboarding/OnboardingContext";
import { FkIcon } from "./Icons";
import { Sidebar, type NavGroup, type NavItem } from "./Sidebar";
import { Topbar, type TopbarBalance, type TopbarUser } from "./Topbar";

export type { NavGroup, NavItem, TopbarBalance, TopbarUser };

/** Ключ свёрнутого меню (прежний, чтобы выбор пользователя пережил редизайн). */
export const COMPACT_KEY = "fishka.studio.compact.v2";

function readCompact(): boolean {
  try {
    return localStorage.getItem(COMPACT_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCompact(value: boolean): void {
  try {
    localStorage.setItem(COMPACT_KEY, String(value));
  } catch {
    /* Меню работает и без хранилища (приватный режим, запрет сайта). */
  }
}

/** Баннер кабинета партнёра под шапкой: чей кабинет и что в нём видно. */
export interface PartnerBannerInfo {
  /** Юрлицо партнёра или список юрлиц. */
  name: string;
  /** «Томск» / «Томск, Северск» / «3 города». */
  place?: string;
}

export function PartnerBanner({ info }: { info: PartnerBannerInfo }) {
  return (
    <div className="fk-partner-banner" role="note">
      <FkIcon name="eye" size={15} />
      <span>
        Кабинет партнёра: <b>{info.name}</b>
        {info.place ? ` · ${info.place}` : ""}. Видны только свои кампании,
        счета и гости; настройки сети скрыты.
      </span>
    </div>
  );
}

/**
 * Оболочка каркаса CRM v2: сайдбар 240 / 64 (свёрнутость запоминается), шапка 56
 * со sticky, баннер партнёра, контент. На <900 px сайдбар выезжает поверх
 * страницы с затемнением (бургер в шапке, Escape и любой переход закрывают).
 */
export function AppShell({
  groups,
  user,
  balance,
  partner,
  onLogout,
  onStartTour,
  tourDone,
  children,
}: {
  groups: NavGroup[];
  user: TopbarUser;
  /** null/undefined — плашки баланса нет. */
  balance?: TopbarBalance | null;
  /** Есть — пользователь в кабинете партнёра, показываем баннер. */
  partner?: PartnerBannerInfo | null;
  onLogout: () => void;
  onStartTour: () => void;
  /** «Пройти обучение заново» вместо «Пройти обучение». */
  tourDone: boolean;
  children: ReactNode;
}) {
  const { pathname, search } = useLocation();
  const { tourStep } = useOnboarding();
  const [menuOpen, setMenuOpen] = useState(false);
  const [compact, setCompact] = useState(readCompact);
  const toggleCompact = () =>
    setCompact((value) => {
      writeCompact(!value);
      return !value;
    });

  // Любой переход и любой шаг тура закрывают мобильное меню (behaviour.md §1.5, §3.2).
  useEffect(() => setMenuOpen(false), [pathname, search, tourStep]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <div
      className={`marketing-root fk-shell${compact ? " fk-compact" : ""}${menuOpen ? " fk-menu-open" : ""}`}
    >
      {menuOpen && (
        <button
          type="button"
          className="fk-shell-overlay"
          aria-label="Закрыть меню"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <Sidebar
        compact={compact}
        onToggleCompact={toggleCompact}
        groups={groups}
        onStartTour={() => {
          setMenuOpen(false);
          onStartTour();
        }}
        tourDone={tourDone}
        onNavigate={() => setMenuOpen(false)}
        focusFirst={menuOpen}
      />
      <div className="fk-workspace">
        <Topbar
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((value) => !value)}
          balance={balance}
          user={user}
          onLogout={onLogout}
        />
        {partner && <PartnerBanner info={partner} />}
        <main className="fk-main">{children}</main>
      </div>
    </div>
  );
}
